import { memo } from 'react';
import {
  Button2 as Button,
  PlaceholderFilterSearch,
  Text,
} from '@frameio/vapor';
import type { SectionId } from '../../../shared/types';
import { LEXICAL_SECTION_ID } from '../../../shared/types';
import TEXT from './generated/TEXT/QuickSearchTabEmptyState';
import styles from './QuickSearchTabEmptyState.module.css';

TEXT.DEFINE({
  /** Title shown when no AI results are found */
  nlpEmptyTitle: 'No AI Results Found',
  /** Description shown when no AI results are found */
  nlpEmptyDescription:
    'Try tweaking your search or see more results under Standard Results.',
  /** Button label to switch to Standard Results with count */
  nlpEmptyButtonLabel: (count: number): string =>
    `View Standard Results (${count})`,
  /** Title shown when no standard results are found */
  lexicalEmptyTitle: 'No Standard Results Found',
  /** Description shown when no standard results are found */
  lexicalEmptyDescription:
    'Try tweaking your search or see more results under AI Results.',
  /** Button label to switch to AI Results with count */
  lexicalEmptyButtonLabel: (count: number): string =>
    `View AI Results (${count})`,
});

type QuickSearchTabEmptyStateProps = {
  activeTab: SectionId;
  otherTabCount: number;
  onAction: () => void;
};

export const QuickSearchTabEmptyState = memo(
  ({ activeTab, otherTabCount, onAction }: QuickSearchTabEmptyStateProps) => {
    const isLexical = activeTab === LEXICAL_SECTION_ID;
    const title = isLexical ? TEXT.lexicalEmptyTitle : TEXT.nlpEmptyTitle;
    const description = isLexical
      ? TEXT.lexicalEmptyDescription
      : TEXT.nlpEmptyDescription;
    const buttonLabel = isLexical
      ? TEXT.lexicalEmptyButtonLabel(otherTabCount)
      : TEXT.nlpEmptyButtonLabel(otherTabCount);

    return (
      <div className={styles.container}>
        <PlaceholderFilterSearch />
        <div className={styles.content}>
          <div className={styles.textGroup}>
            <Text color="primary" variant="header">
              {title}
            </Text>
            <Text color="tertiary" variant="body">
              {description}
            </Text>
          </div>
          <Button onPress={onAction}>{buttonLabel}</Button>
        </div>
      </div>
    );
  }
);

QuickSearchTabEmptyState.displayName = 'QuickSearchTabEmptyState';
